/**
 * Optimal-weight explainer — how a raw score becomes a % weight, in plain
 * language: your score ÷ the total score of the OTHER items in the same pool
 * = your share, which (after a small per-item floor) is your weight. Pools are
 * keyed exactly like the backend (regroup-preview-items.ts): paths by their
 * parent path-group, landers/offers by their owning path. The final step prints
 * the ACTUAL backend weight so it reconciles at rounding/cap edges.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FlowOptimizerItemPreview } from '@trafficpulse/shared';

import { fmtPctFromPerMille, fmtScore } from '../../format';
import type { PreviewRow } from '../../../preview-row.types';
import { toPreviewRow } from '../../../preview-row.types';
import type { AllocatorBounds, CalcStep } from './score-calculation';

/** Pool key — mirrors the backend walker: paths by parent group, landers by
 *  owning path, offers by owning path + parent. */
export function siblingGroupKey(row: PreviewRow): string {
  if (row.layer === 'paths') return `paths|${row.parentId ?? 'flow'}`;
  const pathId = row.pathId ?? row.parentId ?? '?';
  if (row.layer === 'landers') return `landers|${pathId}`;
  return `offers|${pathId}|${row.parentId ?? '?'}`;
}

export function findSiblingRows(
  row: PreviewRow,
  items: ReadonlyArray<FlowOptimizerItemPreview>,
): PreviewRow[] {
  const key = siblingGroupKey(row);
  return items
    .filter((item) => siblingGroupKey(toPreviewRow(item)) === key)
    .map(toPreviewRow);
}

interface OptimalCalc {
  myScore: number;
  totalScore: number;
  n: number;
  min: number;
  reservedPct: number;
  scale: number;
  remaining: number;
  shareFrac: number;
}

/** Weight kept by frozen (pass-through) siblings — competitors share the rest. */
function reservedPct(siblings: PreviewRow[]): number {
  const perMille = siblings
    .filter((s) => s.passThrough && !s.excluded)
    .reduce((sum, s) => sum + (s.currentPerMille ?? 0), 0);
  return Math.max(0, Math.min(100, perMille / 10));
}

function computeOptimalCalc(
  row: PreviewRow,
  competitors: PreviewRow[],
  siblings: PreviewRow[],
  bounds: AllocatorBounds,
): OptimalCalc {
  const n = Math.max(1, competitors.length);
  const min = bounds.minWeight;
  const myScore = Math.max(0, row.rawScore ?? 0);
  const totalScore = competitors.reduce((s, c) => s + Math.max(0, c.rawScore ?? 0), 0);
  const reserved = reservedPct(siblings);
  // The full 100%, NOT `100 - reserved`. Frozen items (skipped by every
  // formula, rule-excluded, no metrics, inactive) reserve NO budget — the
  // competitors split the whole pool and the frozen weights sit on top of it,
  // which is why a pool's displayed weights can add up to more than 100%.
  // See weight-allocator.per-mille.ts:50-56.
  const scale = 100;
  const remaining = Math.max(0, scale - n * min);
  const shareFrac = totalScore > 0 ? myScore / totalScore : 1 / n;
  return { myScore, totalScore, n, min, reservedPct: reserved, scale, remaining, shareFrac };
}

function noun(row: PreviewRow): string {
  const base = row.layer === 'paths' ? 'path' : row.layer === 'landers' ? 'lander' : 'offer';
  return base;
}

export function buildOptimalWeightSteps(
  row: PreviewRow,
  siblings: PreviewRow[],
  bounds: AllocatorBounds,
): CalcStep[] {
  // Defensive: only items in the SAME pool as `row` compete — never sum scores
  // across path groups / other paths' landers & offers.
  const key = siblingGroupKey(row);
  const pool = siblings.filter((s) => siblingGroupKey(s) === key);
  const competitors = pool.filter((s) => s.active && !s.passThrough && !s.excluded);
  const c = computeOptimalCalc(row, competitors, pool, bounds);
  const item = noun(row);
  const optimal = fmtPctFromPerMille(row.optimalPerMille);
  if (c.totalScore <= 0) {
    return [evenSplitStep(c, item, optimal)];
  }
  const sharePct = (c.shareFrac * 100).toFixed(1);
  return [shareStep(c, item, sharePct), optimalStep(c, sharePct, optimal)];
}

function shareStep(c: OptimalCalc, item: string, sharePct: string): CalcStep {
  return {
    label: 'Your share of the pool',
    formula: `your ${fmtScore(c.myScore)} ÷ ${fmtScore(c.totalScore)} (sum of the ${c.n} competing ${item}${c.n > 1 ? 's' : ''})`,
    result: `${sharePct}%`,
  };
}

function optimalStep(c: OptimalCalc, sharePct: string, optimal: string): CalcStep {
  const bonus = (c.shareFrac * c.remaining).toFixed(1);
  const frozen =
    c.reservedPct > 0.05
      ? `; frozen items keep ${c.reservedPct.toFixed(0)}% on top, so the column can add up to more than 100%`
      : '';
  if (c.min > 0) {
    return {
      label: 'Optimal weight',
      formula: `${c.min}% floor + your ${sharePct}% of the ${c.remaining.toFixed(0)}% left${frozen}`,
      result: optimal,
      details: [`${c.min}% + (${sharePct}% × ${c.remaining.toFixed(0)}% = ${bonus}%) ≈ ${optimal}`],
    };
  }
  return {
    label: 'Optimal weight',
    formula: `your ${sharePct}% of the ${c.scale.toFixed(0)}% budget${frozen}`,
    result: optimal,
    details: [`${sharePct}% × ${c.scale.toFixed(0)}% ≈ ${optimal}`],
  };
}

function evenSplitStep(c: OptimalCalc, item: string, optimal: string): CalcStep {
  return {
    label: 'Optimal weight',
    formula: `no ${item} scored above 0 → split the ${c.scale.toFixed(0)}% evenly across ${c.n}`,
    result: optimal,
  };
}
